import { HERO_PRODUCTS, type HeroProduct } from "@marketing/site";

/**
 * The hero's three scenes (a call, a recipe, a chat in Arabic), and where each one is at any moment.
 * Everything on screen (the phone, the 3D cards flying into it) is a pure function of the scene
 * and the time spent in it, so the two layers cannot drift apart and reduced motion simply shows
 * the end of the first scene.
 */

type Trio = [HeroProduct, HeroProduct, HeroProduct];

interface ChatScene {
	kind: "chat";
	/** The scene's tab under the headline: what it shows the product doing. */
	tab: string;
	/** What the scene shows off, over the conversation. */
	label: string;
	user: string;
	step: string;
	reply: string;
	products: Trio;
	/** A recipe answer: the dish, and the amount each product covers (same order as products). */
	recipe?: {
		dish: string;
		serves: number;
		amounts: [string, string, string];
		/** How many other lines the recipe has (pantry items, steps), shown folded. */
		more: string;
	};
}

export interface CallLine {
	who: "caller" | "agent";
	text: string;
	/** An English subtitle under an Arabic line. */
	en?: string;
	/** In a frame: the whole line has been said. */
	done?: boolean;
}

interface CallScene {
	kind: "call";
	tab: string;
	label: string;
	/** Caller, agent (naming the products), caller, agent (confirming). */
	lines: [CallLine, CallLine, CallLine, CallLine];
	products: Trio;
}

export type HeroScene = ChatScene | CallScene;

const P = HERO_PRODUCTS;

export const HERO_SCENES: HeroScene[] = [
	{
		kind: "call",
		tab: "Takes a call",
		label: "Saudi Arabic",
		lines: [
			{
				who: "caller",
				text: "السلام عليكم، أبغى حليب وبيض وخبز لفطور بكرة",
				en: "Hi, I need milk, eggs and bread for tomorrow's breakfast.",
			},
			{
				who: "agent",
				text: "أبشر! حليب المراعي، بيض ١٥ حبة، وخبز عربي. أضيفها لك؟",
				en: "Sure! Almarai milk, 15 eggs and Arabic bread. Shall I add them?",
			},
			{ who: "caller", text: "إيه، أضفها", en: "Yes, add them." },
			{
				who: "agent",
				text: "تم، الثلاثة في سلتك.",
				en: "Done, all three are in your basket.",
			},
		],
		products: [P.milk, P.eggs, P.pita],
	},
	{
		kind: "chat",
		tab: "Cooks a recipe",
		label: "A recipe",
		user: "Chicken shawarma for 4 tonight",
		step: "Recipe for 4 · ingredients matched",
		reply: "Shawarma for 4. I picked the pack that covers each amount:",
		products: [P.chicken, P.garlic, P.pita],
		recipe: {
			dish: "Chicken shawarma",
			serves: 4,
			amounts: ["600 g", "120 g", "400 g"],
			more: "Tomatoes, pickles, spices and 6 steps",
		},
	},
	{
		kind: "chat",
		tab: "Chats in Arabic",
		label: "Gulf Arabic",
		user: "عندكم شيبس ومشروبات حق المباراة؟ نحن ستة",
		step: "Searched snacks and drinks",
		reply: "أكيد! هذي تشكيلة للمباراة تكفي ستة:",
		products: [P.doritos, P.cola, P.cashews],
	},
];

const TYPE_START = 350;
const TYPE_CHAR = 26;
const REPLY_CHAR = 11;
/** Speech pace for the call's captions, close to the agent's real voice. */
const SPEAK_CHAR = 36;
const LINE_GAP = 300;
const CARD_GAP = 200;
/** On a call the cards land one per product name, at the agent's pace. */
const CALL_CARD_GAP = 460;
/** A card starts flying this long before it lands on the phone. */
export const FLIGHT_MS = 640;
const HOLD = 1900;

interface SceneMarks {
	/** Chat: the message is sent. Call: unused (0). */
	sent: number;
	step: number;
	reply: number;
	/** When each call line starts being spoken. */
	lines: number[];
	cards: number;
	added: number;
	end: number;
}

function marks(scene: HeroScene): SceneMarks {
	if (scene.kind === "call") {
		const lines: number[] = [];
		let at = 400;
		for (const line of scene.lines) {
			lines.push(at);
			at += line.text.length * SPEAK_CHAR + LINE_GAP;
		}
		const agentNames = lines[1] ?? 0;
		// The first card lands as the agent says the first name.
		const cards = agentNames + 600 + FLIGHT_MS;
		const added = lines[3] ?? at;
		return {
			sent: 0,
			step: 0,
			reply: 0,
			lines,
			cards,
			added,
			end: at + HOLD,
		};
	}
	const sent = TYPE_START + scene.user.length * TYPE_CHAR + 260;
	const step = sent + 380;
	const reply = step + 720;
	const cards = reply + scene.reply.length * REPLY_CHAR + 200 + FLIGHT_MS;
	const added = cards + CARD_GAP * 3 + 380;
	return { sent, step, reply, lines: [], cards, added, end: added + HOLD };
}

const MARKS = HERO_SCENES.map(marks);

export const sceneDuration = (scene: number) => MARKS[scene]?.end ?? 0;

export interface HeroFrame {
	scene: number;
	/** Chat: what is typed in the box so far. */
	typed: string;
	sent: boolean;
	step: boolean;
	reply: string;
	/** Call: the lines spoken so far, the last one possibly half way. */
	spoken: CallLine[];
	/** Call: who is speaking now. */
	speaking: CallLine["who"] | null;
	/** Call: seconds since it was picked up. */
	elapsed: number;
	cards: number;
	added: boolean;
	/** Items in the basket across the scenes so far. */
	basket: number;
	total: number;
}

const priceOf = (p: HeroProduct) => Number(p.price);

/** What the phone shows `t` ms into `scene`. */
export function frameAt(scene: number, t: number): HeroFrame {
	const s = HERO_SCENES[scene] ?? HERO_SCENES[0];
	const m = MARKS[scene] ?? MARKS[0];
	if (!s || !m) {
		throw new Error("No hero scenes");
	}
	const cardGap = s.kind === "call" ? CALL_CARD_GAP : CARD_GAP;
	const cards = Math.max(
		0,
		Math.min(3, Math.floor((t - m.cards) / cardGap) + 1),
	);
	const added = t >= m.added;
	const earlier = HERO_SCENES.slice(0, scene);
	const basket = earlier.length * 3 + (added ? 3 : 0);
	const total = [
		...earlier.flatMap((e) => e.products),
		...(added ? s.products : []),
	].reduce((sum, p) => sum + priceOf(p), 0);
	const base = { scene, cards, added, basket, total };

	if (s.kind === "call") {
		const spoken: CallLine[] = [];
		let speaking: CallLine["who"] | null = null;
		s.lines.forEach((line, i) => {
			const start = m.lines[i] ?? 0;
			if (t < start) {
				return;
			}
			const chars = Math.min(
				line.text.length,
				Math.floor((t - start) / SPEAK_CHAR),
			);
			spoken.push({
				who: line.who,
				text: line.text.slice(0, chars),
				done: chars === line.text.length,
				...(line.en ? { en: line.en } : {}),
			});
			if (chars < line.text.length) {
				speaking = line.who;
			}
		});
		return {
			...base,
			typed: "",
			sent: false,
			step: false,
			reply: "",
			spoken,
			speaking,
			elapsed: Math.floor(t / 1000),
		};
	}

	const typedChars = Math.max(
		0,
		Math.min(s.user.length, Math.floor((t - TYPE_START) / TYPE_CHAR)),
	);
	const replyChars = Math.max(
		0,
		Math.min(s.reply.length, Math.floor((t - m.reply) / REPLY_CHAR)),
	);
	return {
		...base,
		typed: t >= m.sent ? "" : s.user.slice(0, typedChars),
		sent: t >= m.sent,
		step: t >= m.step,
		reply: t >= m.reply ? s.reply.slice(0, replyChars) : "",
		spoken: [],
		speaking: null,
		elapsed: 0,
	};
}

/** How far each of the scene's three cards is on its flight into the phone (0 to 1, null when not flying). */
export function flightsAt(scene: number, t: number): (number | null)[] {
	const m = MARKS[scene];
	const s = HERO_SCENES[scene];
	if (!m || !s) {
		return [null, null, null];
	}
	const gap = s.kind === "call" ? CALL_CARD_GAP : CARD_GAP;
	return [0, 1, 2].map((i) => {
		const lands = m.cards + i * gap;
		const p = (t - (lands - FLIGHT_MS)) / FLIGHT_MS;
		return p > 0 && p < 1 ? p : null;
	});
}

/** The still frame for reduced motion and the server render: the first scene, finished. */
export const STILL_FRAME = frameAt(0, MARKS[0]?.added ?? 0);
